import { useMemo } from 'react'
import Quill from 'quill'
import { INVITATION_FORMATS, parseDeltaValue } from '../lib/quillDelta'

export interface DeltaRendererProps {
  /**
   * A JSON-serialized Quill Delta document. Strings that don't parse as a
   * Delta are rendered as `pre-wrap` plain text — backward compatibility
   * with legacy invitations written before the wizard switched to Delta.
   */
  value: string | null
  className?: string
  style?: React.CSSProperties
}

/**
 * Convert a Delta to semantic HTML by mounting Quill on a throwaway DOM
 * element and reading `getSemanticHTML()`. Memoized per `value` upstream so
 * we don't spin Quill on every render of the parent modal. Returns null if
 * Quill fails on an unexpected op shape, so the caller degrades to plain text
 * rather than throwing during render and white-screening the modal.
 */
function deltaToHtml(ops: ReadonlyArray<unknown>): string | null {
  const host = document.createElement('div')
  // Quill needs to be attached to the document to compute layout in some
  // code paths; attaching off-screen avoids visible flicker.
  host.style.position = 'absolute'
  host.style.left = '-9999px'
  document.body.appendChild(host)
  try {
    const quill = new Quill(host, {
      readOnly: true,
      formats: [...INVITATION_FORMATS],
      modules: { toolbar: false },
    })
    quill.setContents({ ops } as never, 'silent')
    return quill.getSemanticHTML()
  } catch {
    return null
  } finally {
    host.remove()
  }
}

export function DeltaRenderer({ value, className, style }: DeltaRendererProps) {
  const parsed = useMemo(() => parseDeltaValue(value), [value])
  const html = useMemo(
    () => (parsed.kind === 'delta' ? deltaToHtml(parsed.ops) : null),
    [parsed],
  )

  if (parsed.kind === 'empty') {
    return null
  }

  // A Delta whose HTML failed to build falls back to its raw string as
  // pre-wrap text — never a crash.
  if (parsed.kind === 'delta' && html !== null) {
    // Quill's getSemanticHTML returns formatting tags (b/i/u/a/ol/ul/p),
    // not arbitrary user HTML, so dangerouslySetInnerHTML is safe here for
    // content we just round-tripped through our own editor. The same data
    // never comes from an untrusted source — it's persisted alongside the
    // hunt by team members who already have write access.
    return (
      <div
        className={className}
        style={style}
        data-testid="delta-renderer"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: html ?? '' }}
      />
    )
  }

  return (
    <div
      className={className}
      style={{ whiteSpace: 'pre-wrap', ...style }}
      data-testid="delta-renderer"
    >
      {parsed.kind === 'plain' ? parsed.text : value}
    </div>
  )
}
